import { CalendarMonth, ClearOutlined, AccessTime } from "@mui/icons-material";
import { IconButton, MenuItem, Select } from "@mui/material";
import { DesktopDatePicker, DesktopTimePicker } from "@mui/x-date-pickers";
import { format, isValid } from "date-fns";
import PropTypes from "prop-types";
import React, { forwardRef, useId, useImperativeHandle, useMemo, useRef } from "react";

import {
  getCenturyDateBounds,
  normalizeDateWithinBounds,
} from "../../../../../ui library/helpers/functions";
import { inputStyles, selectStyles } from "../../constant";

const APPT_STATUSES = [
  { value: "Assigned", label: "Scheduled" },
  { value: "Overdue", label: "Missed" },
  { value: "Done", label: "Complete" },
];

function AppointmentForm({ onDelete, canDeleteRow }, ref) {
  const dateInputRef = useRef();
  const timeInputRef = useRef();

  const scheduledDateRef = useRef(null);
  const scheduledTimeRef = useRef(null);
  const dateErrorRef = useRef(false);

  const statusRef = useRef();
  const id = useId();
  const { min: minDate, max: maxDate } = useMemo(() => getCenturyDateBounds(), []);

  const applyNormalizedDate = (value) => {
    const { date, hasError } = normalizeDateWithinBounds(value, minDate, maxDate);
    scheduledDateRef.current = date;
    dateErrorRef.current = hasError;
  };

  useImperativeHandle(ref, () => ({
    getValues: () => {
      applyNormalizedDate(dateInputRef.current?.value ?? scheduledDateRef.current);
      return {
        scheduledDate: scheduledDateRef.current,
        scheduledTime: scheduledTimeRef.current,
        status: statusRef.current?.value ?? "",
      };
    },
    hasDateError: () => dateErrorRef.current,
  }));

  return (
    <div className="flex items-center gap-2">
      <DesktopDatePicker
        closeOnSelect
        inputRef={dateInputRef}
        onChange={(val) => {
          applyNormalizedDate(val);
        }}
        className="w-full"
        minDate={minDate}
        maxDate={maxDate}
        sx={{
          ...inputStyles,
          "& input": { marginLeft: "-8px" },
          "& .MuiOutlinedInput-root": {
            ...inputStyles["& .MuiOutlinedInput-root"],
            "& fieldset": { borderColor: "#9ca3af" },
          },
        }}
        slots={{ OpenPickerIcon: CalendarMonth }}
        slotProps={{
          textField: {
            variant: "outlined",
            onBlur: (event) => {
              applyNormalizedDate(event.target.value);
            },
          },
          inputAdornment: { position: "start" },
        }}
        onError={(reason) => {
          if (!reason || scheduledDateRef.current) {
            dateErrorRef.current = false;
            return;
          }
          dateErrorRef.current = true;
        }}
      />

      <DesktopTimePicker
        closeOnSelect
        inputRef={timeInputRef}
        onChange={(val) => {
          scheduledTimeRef.current = val && isValid(val) ? format(val, "HH:mm:ss") : null;
        }}
        className="w-full"
        sx={{
          ...inputStyles,
          "& input": { marginLeft: "-8px" },
          "& .MuiOutlinedInput-root": {
            ...inputStyles["& .MuiOutlinedInput-root"],
            "& fieldset": { borderColor: "#9ca3af" },
          },
        }}
        slots={{ OpenPickerIcon: AccessTime }}
        slotProps={{
          textField: { variant: "outlined" },
          inputAdornment: { position: "start" },
        }}
      />

      <Select
        id={`${id}_status`}
        className="w-full"
        inputRef={statusRef}
        defaultValue=""
        displayEmpty
        renderValue={(value) =>
          value ? (
            APPT_STATUSES.find((s) => s.value === value)?.label
          ) : (
            <span className="text-gray-400">Appointment Status</span>
          )
        }
        sx={{
          ...selectStyles,
          "& fieldset": { borderColor: "#9ca3af" },
        }}
      >
        {APPT_STATUSES.map((status) => (
          <MenuItem key={status.value} value={status.value}>
            {status.label}
          </MenuItem>
        ))}
      </Select>

      {canDeleteRow && (
        <IconButton color="error" className="m-auto h-fit" onClick={onDelete}>
          <ClearOutlined />
        </IconButton>
      )}
    </div>
  );
}

export default forwardRef(AppointmentForm);

AppointmentForm.propTypes = {
  onDelete: PropTypes.func,
  canDeleteRow: PropTypes.bool,
};
